function safe(s) {
  return s == null ? '' : String(s);
}

function upper(s) {
  const t = safe(s).trim();
  return t ? t.toUpperCase() : '';
}

function fmtPrice(v) {
  if (v == null || v === '' || Number.isNaN(Number(v))) return '';
  const n = Number(v);
  const [intPart, decPart = ''] = n.toFixed(2).split('.');
  const withThousands = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${withThousands},${decPart}`;
}

const merchBodyVertLines = { top: 0, right: 0.15, bottom: 0, left: 0.15 };
const merchGridLine = { top: 0.15, right: 0.15, bottom: 0.15, left: 0.15 };
const merchLineColor = [0, 0, 0];
const MERCH_TEXTO_ALT_FONT_SIZE = 6.5;
/** Media línea (mm) de separación arriba/abajo del texto alternativo en PDF. */
const MERCH_TEXTO_ALT_HALF_LINE_MM = (MERCH_TEXTO_ALT_FONT_SIZE * 0.352778) / 2;
/** Evita partir una fila entre hojas (producto o texto alternativo completo). */
const merchRowKeepTogether = { rowPageBreak: 'avoid' };
/** Solape entre filas para que las verticales no se corten (autotable deja micro-huecos). */
const MERCH_VERT_LINE_OVERLAP = 0.2;

function merchCellLineWidths(cell) {
  const lw = cell?.styles?.lineWidth;
  if (lw == null) return { top: 0, right: 0, bottom: 0, left: 0 };
  if (typeof lw === 'number') {
    return { top: lw, right: lw, bottom: lw, left: lw };
  }
  return { top: 0, right: 0, bottom: 0, left: 0, ...lw };
}

/** Redibuja verticales del body con solape; no agrega horizontales. */
export function drawMerchBodyVerticalOverlap(data) {
  if (data.section !== 'body') return;
  const widths = merchCellLineWidths(data.cell);
  if (widths.left <= 0 && widths.right <= 0) return;

  const doc = data.doc;
  const color = data.cell.styles.lineColor || merchLineColor;
  const rgb = Array.isArray(color) ? color : merchLineColor;
  const { x, y, width, height } = data.cell;
  const yTop = y - MERCH_VERT_LINE_OVERLAP;
  const yBottom = y + height + MERCH_VERT_LINE_OVERLAP;

  doc.setDrawColor(rgb[0], rgb[1], rgb[2]);
  doc.setLineWidth(0.15);
  if (widths.left > 0) doc.line(x, yTop, x, yBottom);
  if (widths.right > 0) doc.line(x + width, yTop, x + width, yBottom);
}

export function textosAlternativosParaPdf(textos) {
  return (Array.isArray(textos) ? textos : [])
    .map((item) => ({
      texto: safe(item?.texto || item?.text).trim(),
      posicion: item?.posicion === 'abajo' ? 'abajo' : 'arriba',
    }))
    .filter((item) => item.texto);
}

/** Fila vacía de 8 celdas: mantiene líneas verticales de la tabla mercancía. */
function merchSpacerRow(heightMm = 2.5) {
  return Array.from({ length: 8 }, (_, col) => ({
    content: '',
    styles: {
      minCellHeight: col === 0 ? heightMm : undefined,
      valign: 'top',
      lineWidth: merchBodyVertLines,
      lineColor: merchLineColor,
    },
  }));
}

function merchEmptyCell(lineWidth = merchBodyVertLines) {
  return { content: '', styles: { lineWidth, lineColor: merchLineColor } };
}

/** Texto alternativo solo en columna MERCHANDISE DESCRIPTION (índice 2). */
function merchTextoAlternativoRow(texto) {
  return [
    merchEmptyCell(),
    merchEmptyCell(),
    {
      content: upper(texto),
      styles: {
        halign: 'left',
        valign: 'top',
        fontSize: MERCH_TEXTO_ALT_FONT_SIZE,
        cellPadding: {
          top: MERCH_TEXTO_ALT_HALF_LINE_MM,
          right: 2,
          bottom: MERCH_TEXTO_ALT_HALF_LINE_MM,
          left: 2,
        },
        lineWidth: merchBodyVertLines,
        lineColor: merchLineColor,
        ...merchRowKeepTogether,
      },
    },
    merchEmptyCell(),
    merchEmptyCell(),
    merchEmptyCell(),
    merchEmptyCell(),
    merchEmptyCell(),
  ];
}

/** Filas de la tabla mercancía: textos alternativos globales + líneas de producto. */
export function buildMerchandiseTableBody(lineas, textosAlternativos = []) {
  const textos = textosAlternativosParaPdf(textosAlternativos);
  const arriba = textos.filter((item) => item.posicion === 'arriba');
  const abajo = textos.filter((item) => item.posicion === 'abajo');

  const productRows = (lineas || []).map((ln) => [
    { content: safe(ln.internalCode), styles: merchRowKeepTogether },
    { content: safe(ln.quantity), styles: merchRowKeepTogether },
    { content: safe(ln.description), styles: { valign: 'top', ...merchRowKeepTogether } },
    { content: safe(ln.djcNum), styles: merchRowKeepTogether },
    { content: safe(ln.djcDate), styles: merchRowKeepTogether },
    { content: safe(ln.currency), styles: merchRowKeepTogether },
    { content: fmtPrice(ln.unitPrice), styles: merchRowKeepTogether },
    { content: fmtPrice(ln.totalValue), styles: merchRowKeepTogether },
  ]);

  const rows = [];
  if (arriba.length) {
    for (const item of arriba) rows.push(merchTextoAlternativoRow(item.texto));
  }
  rows.push(...(productRows.length ? productRows : [['', '', '', '', '', '', '', '']]));
  if (abajo.length) {
    for (const item of abajo) rows.push(merchTextoAlternativoRow(item.texto));
  }
  return rows;
}

export function isMerchSpacerRow(row) {
  return Boolean(row?.[0]?.styles?.minCellHeight) && !merchRowDescription(row);
}

export function merchRowKind(row) {
  if (isMerchSpacerRow(row)) return 'spacer';
  const desc = merchRowDescription(row);
  if (!desc) return 'product';
  const hasOtherData = [0, 1, 3, 4, 5, 6, 7].some((i) => {
    const v = row[i];
    const text = typeof v === 'object' ? safe(v?.content) : safe(v);
    return text.trim() !== '';
  });
  return hasOtherData ? 'product' : 'texto';
}

export function merchRowDescription(row) {
  const cell = row?.[2];
  if (cell == null) return '';
  return typeof cell === 'object' ? safe(cell.content).trim() : safe(cell).trim();
}
